import { useCallback } from 'react';
import { TableWithHeader, TableWithHeaderProps } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import { humanizeDate } from '../../helpers/humanizeDate';
import {
  ConfigurationType,
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { InactiveTag } from '../activity-state/InactiveTag';
import { TableTypography } from '../../common/table/utils';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { PossibleSectionInDependencies } from '../dependencies/search/types';

interface FlowsTableProps extends Pick<TableWithHeaderProps, 'headerVariant'> {
  items: FlowTableItem[];
  onFlowClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  activeItemId?: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

interface FlowTableItem {
  id: string;
  name: string;
  label: string;
  type: string;
  lastModified: string;
  isActive: boolean;
}

const flowsColumns = [
  { field: 'label', headerName: 'Name' },
  { field: 'type', headerName: 'Type' },
  { field: 'lastModified', headerName: 'Last Modified' },
];

const renderRows = ({
  items,
  activeItemId,
  section = 'dependencies',
}: Omit<FlowsTableProps, 'onFlowClick' | 'tableId'>) => {
  return items.map((row, index) => ({
    id: row.id,
    isRowActive: activeItemId === row.id,
    label: (
      <>
        <TableTypography>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType="flows"
            rowIndex={index}
          />
        </TableTypography>
        <InactiveTag isActive={row.isActive} />
      </>
    ),
    type: (
      <TableTypography>
        <TableItemHighlightedText
          text={row.type}
          field="type"
          section={section}
          tableType="flows"
          rowIndex={index}
        />
      </TableTypography>
    ),
    lastModified: (
      <TableTypography>
        <TableItemHighlightedText
          text={humanizeDate({ dateOrTimestamp: row.lastModified })}
          field="lastModified"
          section={section}
          tableType="flows"
          rowIndex={index}
        />
      </TableTypography>
    ),
    isClickable: true,
  }));
};

export const FlowsTable = ({
  items,
  onFlowClick,
  activeItemId,
  clearHistory,
  section = 'dependencies',
  ...props
}: FlowsTableProps) => {
  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      const item = items.find((_item) => _item.id === row.id);
      if (item) {
        onFlowClick({
          id: item.id,
          name: item.name,
          parentType: ConfigurationType.flows,
          clearHistory,
          contentType: DependenciesContentViewType.default,
        });
      }
    },
    [onFlowClick, items, clearHistory],
  );

  return (
    <TableWithHeader
      tableHeader="Flows"
      key="tableHeader_flows"
      columns={flowsColumns}
      rows={renderRows({ items, activeItemId, section })}
      onRowClick={onRowClick}
      {...props}
      dataTableType="flows"
      section={section}
    />
  );
};
